import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Ticket, Calendar, MapPin, QrCode, ExternalLink, Tag, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.fanbound.live';

export default function MyTickets() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showResaleModal, setShowResaleModal] = useState(false);
  const [resalePrice, setResalePrice] = useState('');
  const [listing, setListing] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchTickets();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/tickets/my-tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleListForResale = async () => {
    if (!selectedTicket || !resalePrice) return;
    
    const price = parseFloat(resalePrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setListing(true);
    try {
      await axios.post(`${API_URL}/resale/list`, {
        ticket_id: selectedTicket.id,
        asking_price: price
      });
      toast.success('Ticket listed for resale!');
      setShowResaleModal(false);
      setSelectedTicket(null);
      setResalePrice('');
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to list ticket');
    } finally {
      setListing(false);
    }
  };

  if (authLoading || (!isAuthenticated && !authLoading)) {
    return null;
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">My Tickets</h1>
            <p className="text-muted-foreground">Your purchased tickets and upcoming events</p>
          </div>
          <Link to="/resale">
            <Button variant="outline" className="gap-2">
              <Tag className="w-4 h-4" />
              Resale Market
            </Button>
          </Link>
        </div>

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card border border-white/10 rounded-xl p-6 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-white/10 rounded-lg" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-white/10 rounded w-3/4" />
                    <div className="h-4 bg-white/10 rounded w-1/2" />
                    <div className="h-4 bg-white/10 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 bg-card border border-white/10 rounded-xl">
            <Ticket className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-xl mb-2">No tickets yet</h3>
            <p className="text-muted-foreground mb-6">
              You haven't purchased any tickets yet. Browse events to find your next experience!
            </p>
            <Button onClick={() => navigate('/events')} data-testid="browse-events-btn">
              Browse Events
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <div 
                key={ticket.id}
                className="bg-card border border-white/10 rounded-xl overflow-hidden hover:border-primary/30 transition-colors"
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* QR Code - Larger and more prominent */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="w-32 h-32 sm:w-40 sm:h-40 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                        <QRCodeSVG 
                          value={ticket.qr_url || `${FRONTEND_URL}/ticket/verify/${ticket.qr_code}`}
                          size={140}
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Scan at entry
                      </p>
                    </div>
                    
                    {/* Ticket Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-heading font-semibold text-lg mb-1">
                            {ticket.event_title}
                          </h3>
                          <Badge variant="secondary" className="mb-3">
                            {ticket.tier_name}
                          </Badge>
                        </div>
                        <Badge 
                          className={
                            ticket.status === 'valid' || ticket.status === 'active'
                              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                              : ticket.status === 'used'
                              ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }
                        >
                          {ticket.status === 'valid' ? 'Active' : ticket.status}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Purchased {format(new Date(ticket.purchased_at), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Ticket className="w-4 h-4" />
                          ${ticket.price_paid}
                        </span>
                      </div>
                      
                      {/* Full Ticket ID */}
                      <div className="bg-secondary/50 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-1">Ticket ID</p>
                        <p className="font-mono text-sm break-all">{ticket.qr_code}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Ticket Footer */}
                <div className="px-6 py-3 bg-secondary/30 border-t border-white/5 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Ticket #{ticket.id.slice(0, 8)}
                  </span>
                  <div className="flex items-center gap-2">
                    {ticket.status === 'active' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setResalePrice(Math.round(ticket.price_paid * 1.1).toString());
                          setShowResaleModal(true);
                        }}
                        data-testid={`resale-btn-${ticket.id}`}
                      >
                        <Tag className="w-3 h-3" />
                        Sell
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate(`/events/${ticket.event_id}`)}>
                      View Event <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resale Modal */}
      <Dialog open={showResaleModal} onOpenChange={setShowResaleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>List Ticket for Resale</DialogTitle>
            <DialogDescription>
              Set your asking price for this ticket
            </DialogDescription>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 py-4">
              <div className="bg-secondary/30 rounded-lg p-4">
                <h3 className="font-heading font-semibold mb-1">{selectedTicket.event_title}</h3>
                <p className="text-sm text-muted-foreground">{selectedTicket.tier_name}</p>
                <p className="text-sm text-muted-foreground">Original price: ${selectedTicket.price_paid}</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Asking Price (includes 30% platform fee)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="number"
                    value={resalePrice}
                    onChange={(e) => setResalePrice(e.target.value)}
                    className="pl-10"
                    min="1"
                    step="0.01"
                    data-testid="resale-price-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  You'll receive ~${(parseFloat(resalePrice || 0) * 0.7).toFixed(2)} after platform fee
                </p>
              </div>

              <Button 
                className="w-full gap-2" 
                onClick={handleListForResale}
                disabled={listing || !resalePrice}
                data-testid="confirm-resale-listing"
              >
                {listing ? 'Listing...' : (
                  <>
                    <Tag className="w-4 h-4" />
                    List for Sale
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
